/**
 * CoPilotRail — sticky right-rail AI agent.
 *
 * Always-on chat. Knows the active order context. Recognizes a small
 * set of intents (negotiation, certs, customs, invoice, etc.) and can
 * fire action chips that update other parts of the page (switch tab,
 * mark step done, post message, etc.).
 *
 * Intent matching is deliberately naive: regex keyword lookup against
 * the prompt list. Demo-grade only.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, ArrowRight, Bot } from 'lucide-react';
import { useManifest } from '../ManifestContext';
import { getOrderMocks, RESPONSES, FALLBACK_REPLY, ACTION_EFFECTS } from '../mocks';

const renderMarkdownLite = (text) => {
  // Lightweight markdown: **bold** and _italic_, plus newline → <br/>
  const parts = [];
  let remaining = text;
  let key = 0;
  const re = /(\*\*([^*]+)\*\*|_([^_]+)_)/;
  while (remaining.length) {
    const m = remaining.match(re);
    if (!m) {
      parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }
    if (m.index > 0) {
      parts.push(<React.Fragment key={key++}>{remaining.slice(0, m.index)}</React.Fragment>);
    }
    if (m[2]) parts.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3]) parts.push(<em key={key++}>{m[3]}</em>);
    remaining = remaining.slice(m.index + m[0].length);
  }
  return parts.map((p, i) => {
    if (typeof p === 'object' && p.type === React.Fragment && typeof p.props.children === 'string') {
      return p.props.children.split('\n').map((line, j, arr) => (
        <React.Fragment key={`${i}-${j}`}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return p;
  });
};

const CoPilotRail = ({ onTabChange, onAction }) => {
  const { activeOrder } = useManifest();
  const mocks = getOrderMocks(activeOrder.id);
  const [messages, setMessages] = useState(mocks.coPilot.initial);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [toast, setToast] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages(getOrderMocks(activeOrder.id).coPilot.initial);
  }, [activeOrder.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const findResponse = (text) => {
    for (const r of RESPONSES) {
      if (r.match.test(text)) return r;
    }
    return null;
  };

  const sendMessage = (text) => {
    if (!text?.trim()) return;
    const userMsg = { id: `u-${Date.now()}`, sender: 'user', body: text, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setThinking(true);

    const r = findResponse(text);
    setTimeout(() => {
      const replyBody = r ? r.reply(activeOrder) : FALLBACK_REPLY(activeOrder);
      const replyActions = r?.actions || [];
      setMessages((m) => [
        ...m,
        {
          id: `a-${Date.now()}`,
          sender: 'agent',
          body: replyBody,
          actions: replyActions,
          ts: new Date().toISOString(),
        },
      ]);
      setThinking(false);
    }, 700);
  };

  const handleAction = (action) => {
    const eff = ACTION_EFFECTS[action.id] || { toast: 'Action triggered.' };
    if (eff.toast) {
      setToast(eff.toast);
      setTimeout(() => setToast(null), 2200);
    }
    if (eff.goTab && onTabChange) onTabChange(eff.goTab);
    if (onAction) onAction(action.id, eff);
  };

  return (
    <aside className="manifest-copilot">
      <div className="manifest-copilot-head">
        <div className="manifest-copilot-avatar"><Bot size={18} /></div>
        <div>
          <div className="manifest-copilot-title">Manifest Co-Pilot</div>
          <div className="manifest-copilot-sub">Knows this contract. Can take actions.</div>
        </div>
      </div>

      <div className="manifest-copilot-thread" ref={scrollRef}>
        {messages.map((m) => (
          <div key={m.id} className={`manifest-copilot-msg ${m.sender}`}>
            {m.sender === 'agent' && <div className="manifest-copilot-msg-author"><Sparkles size={11} /> Co-Pilot</div>}
            <div className="manifest-copilot-msg-body">{renderMarkdownLite(m.body)}</div>
            {m.actions?.length > 0 && (
              <div className="manifest-copilot-msg-actions">
                {m.actions.map((a) => (
                  <button key={a.id} type="button" className="manifest-copilot-action-chip" onClick={() => handleAction(a)}>
                    <ArrowRight size={11} /> {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {thinking && (
          <div className="manifest-copilot-msg agent">
            <div className="manifest-copilot-msg-author"><Sparkles size={11} /> Co-Pilot</div>
            <div className="manifest-copilot-msg-body manifest-copilot-thinking">
              <span className="dot" /><span className="dot" /><span className="dot" />
            </div>
          </div>
        )}
      </div>

      {mocks.coPilot.suggested.length > 0 && (
        <div className="manifest-copilot-suggested">
          <div className="manifest-copilot-suggested-label">Try asking</div>
          <div className="manifest-copilot-suggested-list">
            {mocks.coPilot.suggested.map((p, i) => (
              <button key={i} type="button" className="manifest-copilot-suggested-chip" onClick={() => sendMessage(p)}>
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      <form
        className="manifest-copilot-input"
        onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
      >
        <input
          type="text"
          placeholder="Ask the Co-Pilot anything…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button type="submit" className="manifest-copilot-send" disabled={!input.trim()}>
          <Send size={14} />
        </button>
      </form>

      {toast && <div className="manifest-copilot-toast">{toast}</div>}
    </aside>
  );
};

export default CoPilotRail;
